import React, { cloneElement } from "react";
import { ZillowMapControl } from "@zillow/zillow-map-control";
import { func, node, number, shape, string } from "prop-types";

import { useZoom } from "../../utils/customHooks";
import ZoomControls from "../ZoomControls";

const ZoomableMap = ({
  apiKey,
  children,
  gestureHandling,
  lat,
  long,
  mapCenter,
  mapId,
  mapRef,
  maxZoom,
  minZoom,
  onBoundsChange,
  onClickMapAnywhere,
  onReady,
  zoomLevel
}) => {
  const [zoom, handleZoomIn, handleZoomOut, onZoomChanged] = useZoom(
    maxZoom,
    minZoom,
    zoomLevel
  );

  const getMapCenter = () => {
    if (mapCenter) {
      return mapCenter;
    }
    return {
      northSouthLat: lat,
      eastWestLng: long
    };
  };

  const childrenWithAddedProps = React.Children.toArray(children).map(child =>
    cloneElement(child, { zoom })
  );

  return (
    <ZillowMapControl
      ref={mapRef}
      apiKey={apiKey}
      gestureHandling={gestureHandling}
      id={mapId}
      mapCenter={getMapCenter()}
      maxZoom={maxZoom}
      minZoom={minZoom}
      onBoundsChange={onBoundsChange}
      onClickMapAnywhere={onClickMapAnywhere}
      onReady={onReady}
      onZoomChanged={onZoomChanged}
      zoomControl
      zoomLevel={zoom}
    >
      <ZoomControls
        disableZoomIn={zoom === maxZoom}
        disableZoomOut={zoom === minZoom}
        zoomIn={handleZoomIn}
        zoomOut={handleZoomOut}
      />
      {childrenWithAddedProps}
    </ZillowMapControl>
  );
};

const validateCoords = (lat, long) => {
  if (!lat || !long) return false;
  return typeof lat === "number" && typeof long === "number";
};

const validateMapCenter = mapCenter => {
  if (!mapCenter) return false;
  return validateCoords(mapCenter.northSouthLat, mapCenter.eastWestLng);
};

const validateCenter = (props, propName, componentName) => {
  const validCoords = validateCoords(props.lat, props.long);
  const validMapCenter = validateMapCenter(props.mapCenter);

  if (!validMapCenter && !validCoords) {
    return new Error(
      `One of props 'mapCenter' or 'lat' and 'long' were  not defined in '${componentName}'.`
    );
  }
  return null;
};

ZoomableMap.propTypes = {
  apiKey: string,
  children: node,
  gestureHandling: string,
  lat: validateCenter,
  long: validateCenter,
  mapCenter: validateCenter,
  mapId: string.isRequired,
  mapRef: shape({}),
  maxZoom: number,
  minZoom: number,
  onBoundsChange: func,
  onClickMapAnywhere: func,
  onReady: func,
  zoomLevel: number.isRequired
};

ZoomableMap.defaultProps = {
  apiKey: process.env.GOOGLE_MAPS_API_KEY,
  children: null,
  gestureHandling: "auto",
  lat: null,
  long: null,
  mapCenter: null,
  mapRef: null,
  maxZoom: 19,
  minZoom: 12,
  onBoundsChange: () => {},
  onClickMapAnywhere: () => {},
  onReady: () => {}
};

export default ZoomableMap;
